import { DEFAULT_DPI } from '@/domains/scan/services/configurationDatas';
import { LaserExtractionEnum } from '@/domains/scan/typescript/enums/LaserExtractionEnum';
import type DeviceInterface from '@/domains/scan/typescript/interfaces/DeviceInterface';
import type HelloInterface from '@/domains/scan/typescript/interfaces/HelloInterface';
import type MessageInterface from '@/domains/scan/typescript/interfaces/MessageInterface';
import type ScanInterface from '@/domains/scan/typescript/interfaces/ScanInterface';
import type ScanStatusInterface from '@/domains/scan/typescript/interfaces/ScanStatusInterface';

const SCAN_API_URI = import.meta.env.VITE_SCAN_API_URI;

const hello = async (): Promise<HelloInterface> => {
    const response = await fetch(`${SCAN_API_URI}/hello`);

    return response.json();
};

const getListDevices = async (): Promise<DeviceInterface[]> => {
    const response = await fetch(`${SCAN_API_URI}/devices/`);

    return response.json();
};

const getDeviceInfo = async (deviceId: string): Promise<DeviceInterface | MessageInterface> => {
    const encodedId = encodeURI(deviceId);
    const response = await fetch(`${SCAN_API_URI}/devices/${encodedId}/`);

    return response.json();
};

async function getLogs(): Promise<Blob> {
    const response = await fetch(`${SCAN_API_URI}/logs/podonex_logs.csv`);
    return response.blob();
}

async function getPodonexClientIPCTimeout(): Promise<number | undefined> {
    const response = await fetch(`${SCAN_API_URI}/config`);
    const json = await response.json();
    return json.podonex.ipc_timeout;
}

async function setPodonexClientIPCTimeout(ipcTimeout: number) {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify({ podonex: { ipc_timeout: ipcTimeout } }),
        headers: {
            'Content-Type': 'application/json',
        },
    };

    await fetch(`${SCAN_API_URI}/config`, requestOptions);
}
const askNewScan = async (
    deviceId: string,
    {
        backend,
        brightness,
        contrast,
        lightSensibility,
        useButtonTrigger,
        laserExtraction,
    }: {
        backend: string;
        brightness?: number;
        contrast?: number;
        lightSensibility?: string;
        useButtonTrigger?: boolean;
        laserExtraction?: LaserExtractionEnum;
    },
): Promise<ScanInterface | MessageInterface> => {
    const encodedId = encodeURI(deviceId);

    const requestOptions: RequestInit = {
        method: 'POST',
    };

    const options: Record<string, number | string> = {};

    if (backend === 'wia') {
        options['Horizontal Resolution'] = DEFAULT_DPI;
        options['Vertical Resolution'] = DEFAULT_DPI;

        if (brightness) {
            options.Brightness = brightness;
        }

        if (contrast) {
            options.Contrast = contrast;
        }
    }

    if (backend === 'scanline') {
        options.resolution = DEFAULT_DPI;
    }

    if (backend === 'fusion3d') {
        if (lightSensibility) {
            options.sensitivity = lightSensibility;
        }
        if (laserExtraction) {
            options.laser_extraction = laserExtraction;
        }
    }

    if (Object.values(options).length > 0) {
        requestOptions.headers = {
            'Content-Type': 'application/json',
        };
        requestOptions.body = JSON.stringify(options);
    }

    const url = new URL(`${SCAN_API_URI}/devices/${encodedId}/new-scan`);
    if (useButtonTrigger) {
        url.searchParams.append('use_button_trigger', 'true');
    }

    const response = await fetch(url, requestOptions);

    return response.json();
};

const getListScans = async (): Promise<string[]> => {
    const response = await fetch(`${SCAN_API_URI}/scans/`);

    return response.json();
};

const getScanStatus = async (scanId: string): Promise<ScanStatusInterface | MessageInterface> => {
    const response = await fetch(`${SCAN_API_URI}/scans/${scanId}/`);

    return response.json();
};

const getScanFiles = async (scanId: string): Promise<string[] | MessageInterface> => {
    const response = await fetch(`${SCAN_API_URI}/scans/${scanId}/files/`);

    return response.json();
};

const getScanFile = async (params: { scanId: string; fileId: string }): Promise<Blob | MessageInterface> => {
    const response = await fetch(`${SCAN_API_URI}/scans/${params.scanId}/files/${params.fileId}`);
    const headersContentType = response.headers.get('Content-type');
    if (headersContentType === 'application/json') {
        return response.json();
    }

    return response.blob();
};

const deleteScan = async (scanId: string): Promise<MessageInterface> => {
    const response = await fetch(`${SCAN_API_URI}/scans/${scanId}`, {
        method: 'DELETE',
    });

    return response.json();
};

async function cancelButtonScan() {
    const response = await fetch(`${SCAN_API_URI}/status/awaiting-button-trigger`, {
        method: 'DELETE',
    });

    return response.json();
}

export default {
    hello,
    getListDevices,
    getDeviceInfo,
    askNewScan,
    getListScans,
    getScanStatus,
    getScanFiles,
    getScanFile,
    deleteScan,
    getLogs,
    cancelButtonScan,
    getPodonexClientIPCTimeout,
    setPodonexClientIPCTimeout,
};
